"use client";

import createCorporateApplication from "@/app/egitime-destek/_actions/create-corporate-application";
import createEducationApplication from "@/app/egitime-destek/_actions/create-education-application";
import createIndividualApplication from "@/app/egitime-destek/_actions/create-individual-application";
import {
    buildCorporateApplicationSchema,
    buildEducationApplicationSchema,
    buildIndividualApplicationSchema,
    CorporateApplicationFormData,
    EducationApplicationFormData,
    IndividualApplicationFormData,
} from "@/app/egitime-destek/_actions/schema";
import { PhoneInputFormField } from "@/components/form/phone-input-form-field";
import { TextInputFormField } from "@/components/form/text-input-form-field";
import { Button } from "@/components/shadcn/button";
import { FieldGroup } from "@/components/shadcn/field";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/shadcn/tabs";
import TurnstileWidget, {
    type TurnstileWidgetHandle,
} from "@/components/turnstile-widget";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

function useCaptcha() {
    const [captchaToken, setCaptchaToken] = useState("");
    const captchaRef = useRef<TurnstileWidgetHandle>(null);
    return { captchaToken, setCaptchaToken, captchaRef };
}

function CorporateTabForm() {
    const t = useTranslations("support");
    const { captchaToken, setCaptchaToken, captchaRef } = useCaptcha();
    const form = useForm<CorporateApplicationFormData>({
        resolver: zodResolver(buildCorporateApplicationSchema(t)),
        defaultValues: {
            companyName: "",
            companyPhone: "",
            companyContact: "",
            companyTitle: "",
            companyEmail: "",
        },
    });

    const mutation = useMutation({
        mutationFn: async (data: CorporateApplicationFormData) =>
            createCorporateApplication(data, captchaToken),
        onSuccess: (result) => {
            if (result.success) {
                toast.success(t("successTitle"), {
                    description: result.message,
                });
                form.reset();
            } else {
                toast.error(t("errorTitle"), {
                    description: result.error.message,
                });
            }
        },
        onError: () => {
            toast.error(t("errorTitle"), {
                description: t("unexpectedError"),
            });
        },
        onSettled: () => {
            captchaRef.current?.reset();
        },
    });

    const onSubmit = form.handleSubmit((data) => {
        if (!captchaToken) {
            toast.error(t("errorTitle"), {
                description: t("turnstilePending"),
            });
            return;
        }
        mutation.mutate(data);
    });

    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-6">
            <FieldGroup className="gap-4">
                <TextInputFormField
                    control={form.control}
                    name="companyName"
                    label={t("companyName")}
                    placeholder={t("companyNamePlaceholder")}
                    required
                />
                <PhoneInputFormField
                    control={form.control}
                    name="companyPhone"
                    label={t("phone")}
                    placeholder="(555) 000-0000"
                    required
                />
                <TextInputFormField
                    control={form.control}
                    name="companyContact"
                    label={t("companyContact")}
                    placeholder={t("namePlaceholder")}
                    required
                />
                <TextInputFormField
                    control={form.control}
                    name="companyTitle"
                    label={t("companyTitle")}
                    placeholder={t("titlePlaceholder")}
                    required
                />
                <TextInputFormField
                    control={form.control}
                    name="companyEmail"
                    label={t("email")}
                    placeholder="ornek@firma.com"
                    required
                />
                <TurnstileWidget
                    ref={captchaRef}
                    onToken={setCaptchaToken}
                    action="egitimeDestekSubmit"
                />
            </FieldGroup>
            <Button
                type="submit"
                className="w-full"
                disabled={mutation.isPending || !captchaToken}
                variant="default"
            >
                {mutation.isPending ? t("sending") : t("submit")}
            </Button>
        </form>
    );
}

function IndividualTabForm() {
    const t = useTranslations("support");
    const { captchaToken, setCaptchaToken, captchaRef } = useCaptcha();
    const form = useForm<IndividualApplicationFormData>({
        resolver: zodResolver(buildIndividualApplicationSchema(t)),
        defaultValues: {
            nameSurname: "",
            phone: "",
            email: "",
        },
    });

    const mutation = useMutation({
        mutationFn: async (data: IndividualApplicationFormData) =>
            createIndividualApplication(data, captchaToken),
        onSuccess: (result) => {
            if (result.success) {
                toast.success(t("successTitle"), {
                    description: result.message,
                });
                form.reset();
            } else {
                toast.error(t("errorTitle"), {
                    description: result.error.message,
                });
            }
        },
        onError: () => {
            toast.error(t("errorTitle"), {
                description: t("unexpectedError"),
            });
        },
        onSettled: () => {
            captchaRef.current?.reset();
        },
    });

    const onSubmit = form.handleSubmit((data) => {
        if (!captchaToken) {
            toast.error(t("errorTitle"), {
                description: t("turnstilePending"),
            });
            return;
        }
        mutation.mutate(data);
    });

    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-6">
            <FieldGroup className="gap-4">
                <TextInputFormField
                    control={form.control}
                    name="nameSurname"
                    label={t("nameSurname")}
                    placeholder={t("namePlaceholder")}
                    required
                />
                <PhoneInputFormField
                    control={form.control}
                    name="phone"
                    label={t("phone")}
                    placeholder="(555) 000-0000"
                    required
                />
                <TextInputFormField
                    control={form.control}
                    name="email"
                    label={t("email")}
                    placeholder="ornek@email.com"
                    required
                />
                <TurnstileWidget
                    ref={captchaRef}
                    onToken={setCaptchaToken}
                    action="egitimeDestekSubmit"
                />
            </FieldGroup>
            <Button
                type="submit"
                className="w-full"
                disabled={mutation.isPending || !captchaToken}
                variant="default"
            >
                {mutation.isPending ? t("sending") : t("submit")}
            </Button>
        </form>
    );
}

export function SupporterApplicationForm() {
    const t = useTranslations("support");
    return (
        <Tabs defaultValue="corporate" className="flex flex-col gap-6">
            <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="corporate">{t("corporate")}</TabsTrigger>
                <TabsTrigger value="individual">{t("individual")}</TabsTrigger>
            </TabsList>
            <TabsContent value="corporate" className="mt-0">
                <CorporateTabForm />
            </TabsContent>
            <TabsContent value="individual" className="mt-0">
                <IndividualTabForm />
            </TabsContent>
        </Tabs>
    );
}

export function EducationRequestForm() {
    const t = useTranslations("support");
    const { captchaToken, setCaptchaToken, captchaRef } = useCaptcha();
    const form = useForm<EducationApplicationFormData>({
        resolver: zodResolver(buildEducationApplicationSchema(t)),
        defaultValues: {
            schoolName: "",
            schoolPhone: "",
            schoolContact: "",
            schoolTitle: "",
            schoolEmail: "",
            schoolCount: "",
        },
    });

    const mutation = useMutation({
        mutationFn: async (data: EducationApplicationFormData) =>
            createEducationApplication(data, captchaToken),
        onSuccess: (result) => {
            if (result.success) {
                toast.success(t("successTitle"), {
                    description: result.message,
                });
                form.reset();
            } else {
                toast.error(t("errorTitle"), {
                    description: result.error.message,
                });
            }
        },
        onError: () => {
            toast.error(t("errorTitle"), {
                description: t("unexpectedError"),
            });
        },
        onSettled: () => {
            captchaRef.current?.reset();
        },
    });

    const onSubmit = form.handleSubmit((data) => {
        if (!captchaToken) {
            toast.error(t("errorTitle"), {
                description: t("turnstilePending"),
            });
            return;
        }
        mutation.mutate(data);
    });

    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-6">
            <FieldGroup className="gap-4">
                <TextInputFormField
                    control={form.control}
                    name="schoolName"
                    label={t("schoolName")}
                    placeholder={t("schoolNamePlaceholder")}
                    required
                />
                <PhoneInputFormField
                    control={form.control}
                    name="schoolPhone"
                    label={t("phone")}
                    placeholder="(555) 000-0000"
                    required
                />
                <TextInputFormField
                    control={form.control}
                    name="schoolContact"
                    label={t("schoolContact")}
                    placeholder={t("namePlaceholder")}
                    required
                />
                <TextInputFormField
                    control={form.control}
                    name="schoolTitle"
                    label={t("schoolTitle")}
                    placeholder={t("schoolTitlePlaceholder")}
                    required
                />
                <TextInputFormField
                    control={form.control}
                    name="schoolEmail"
                    label={t("email")}
                    placeholder="ornek@okul.com"
                    required
                />
                <TextInputFormField
                    control={form.control}
                    name="schoolCount"
                    label={t("schoolCount")}
                    placeholder={t("schoolCountPlaceholder")}
                    required
                />
                <TurnstileWidget
                    ref={captchaRef}
                    onToken={setCaptchaToken}
                    action="egitimeDestekSubmit"
                />
            </FieldGroup>
            <Button
                type="submit"
                className="w-full"
                disabled={mutation.isPending || !captchaToken}
                variant="default"
            >
                {mutation.isPending ? t("sending") : t("submit")}
            </Button>
        </form>
    );
}
